import { useState, useEffect, useCallback } from "react";

const STORAGE_KEY = "cap_cookie_prefs_v1";

const DEFAULTS = {
    necessary: true,
    analytics: false,
    marketing: false,
    decided: false,
};

function readPrefs() {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return { ...DEFAULTS };
        return { ...DEFAULTS, ...JSON.parse(raw) };
    } catch {
        return { ...DEFAULTS };
    }
}

function writePrefs(prefs) {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...prefs, decided: true }));
    } catch {
        /* noop */
    }
}

export default function CookieConsent() {
    const [open, setOpen] = useState(false);
    const [prefsOpen, setPrefsOpen] = useState(false);
    const [prefs, setPrefs] = useState(DEFAULTS);

    useEffect(() => {
        const current = readPrefs();
        setPrefs(current);
        if (!current.decided) {
            const t = setTimeout(() => setOpen(true), 400);
            return () => clearTimeout(t);
        }
    }, []);

    useEffect(() => {
        const openPrefs = () => {
            setPrefs(readPrefs());
            setPrefsOpen(true);
            setOpen(false);
        };
        window.addEventListener("cap:open-cookie-prefs", openPrefs);
        return () => window.removeEventListener("cap:open-cookie-prefs", openPrefs);
    }, []);

    const acceptAll = useCallback(() => {
        const next = { necessary: true, analytics: true, marketing: true, decided: true };
        setPrefs(next);
        writePrefs(next);
        setOpen(false);
        setPrefsOpen(false);
    }, []);

    const rejectNonEssential = useCallback(() => {
        const next = { necessary: true, analytics: false, marketing: false, decided: true };
        setPrefs(next);
        writePrefs(next);
        setOpen(false);
        setPrefsOpen(false);
    }, []);

    const savePrefs = useCallback(() => {
        writePrefs(prefs);
        setPrefsOpen(false);
        setOpen(false);
    }, [prefs]);

    return (
        <>
            {open && (
                <div
                    role="dialog"
                    aria-label="Cookie consent"
                    data-testid="cookie-banner"
                    className="fixed bottom-6 left-6 right-6 md:left-auto md:right-6 md:max-w-md z-50 bg-offwhite border border-navy shadow-sm p-6"
                >
                    <div className="cap-eyebrow mb-3">Cookie Preferences</div>
                    <h3 className="font-serif text-navy text-2xl leading-tight mb-3">
                        We respect your privacy.
                    </h3>
                    <p className="text-sm text-slate leading-relaxed mb-5">
                        We use strictly necessary cookies to operate the site. With your
                        consent, we also use analytics and marketing cookies to improve
                        our work. You can manage preferences at any time.
                    </p>
                    <div className="flex flex-col gap-2">
                        <button
                            type="button"
                            onClick={acceptAll}
                            data-testid="cookie-accept-all-btn"
                            className="cap-btn-primary w-full"
                        >
                            Accept all
                        </button>
                        <button
                            type="button"
                            onClick={rejectNonEssential}
                            data-testid="cookie-reject-btn"
                            className="cap-btn-secondary w-full"
                        >
                            Reject non-essential
                        </button>
                        <button
                            type="button"
                            onClick={() => {
                                setPrefsOpen(true);
                                setOpen(false);
                            }}
                            data-testid="cookie-manage-btn"
                            className="text-sm text-slate underline underline-offset-4 hover:text-navy mt-1"
                        >
                            Manage preferences
                        </button>
                    </div>
                </div>
            )}

            {prefsOpen && (
                <div
                    role="dialog"
                    aria-modal="true"
                    aria-label="Manage cookie preferences"
                    data-testid="cookie-prefs-modal"
                    className="fixed inset-0 z-50 bg-navy/50 flex items-end md:items-center justify-center p-4"
                >
                    <div className="bg-offwhite border border-navy max-w-lg w-full p-8">
                        <div className="cap-eyebrow mb-3">Preferences</div>
                        <h3 className="font-serif text-navy text-3xl mb-2">
                            Manage cookie preferences
                        </h3>
                        <p className="text-sm text-slate mb-6">
                            Choose which categories of cookies you are comfortable with.
                            Strictly necessary cookies are always active.
                        </p>

                        <div className="space-y-4">
                            <PrefRow
                                testId="cookie-pref-necessary"
                                title="Strictly necessary"
                                description="Required for the website to function. Cannot be disabled."
                                checked
                                disabled
                            />
                            <PrefRow
                                testId="cookie-pref-analytics"
                                title="Analytics"
                                description="Help us understand how visitors use the site so we can improve it."
                                checked={prefs.analytics}
                                onChange={(v) => setPrefs((p) => ({ ...p, analytics: v }))}
                            />
                            <PrefRow
                                testId="cookie-pref-marketing"
                                title="Marketing"
                                description="Used to measure the effectiveness of our communications."
                                checked={prefs.marketing}
                                onChange={(v) => setPrefs((p) => ({ ...p, marketing: v }))}
                            />
                        </div>

                        <div className="mt-8 flex flex-col sm:flex-row gap-3">
                            <button
                                type="button"
                                onClick={savePrefs}
                                data-testid="cookie-save-prefs-btn"
                                className="cap-btn-primary flex-1"
                            >
                                Save preferences
                            </button>
                            <button
                                type="button"
                                onClick={acceptAll}
                                data-testid="cookie-accept-all-in-modal-btn"
                                className="cap-btn-secondary flex-1"
                            >
                                Accept all
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

function PrefRow({ title, description, checked, onChange, disabled, testId }) {
    return (
        <div className="flex items-start justify-between gap-6 border border-sand p-4">
            <div>
                <div className="font-serif text-navy text-lg leading-tight">{title}</div>
                <div className="text-xs text-slate mt-1 leading-relaxed">{description}</div>
            </div>
            <label className={`relative inline-flex items-center cursor-pointer shrink-0 mt-1 ${disabled ? "opacity-60 cursor-not-allowed" : ""}`}>
                <input
                    type="checkbox"
                    className="sr-only peer"
                    checked={checked}
                    disabled={disabled}
                    onChange={(e) => onChange && onChange(e.target.checked)}
                    data-testid={testId}
                />
                <span className="w-10 h-5 bg-sand peer-checked:bg-navy transition-colors relative">
                    <span
                        className={`absolute top-0.5 left-0.5 bg-offwhite h-4 w-4 transition-transform ${
                            checked ? "translate-x-5" : ""
                        }`}
                    />
                </span>
            </label>
        </div>
    );
}
